/**
 * An ⓘ button that opens a small explanatory card next to a section heading.
 * Same open/close pattern as the variance sensitivity cog: anchored Popover,
 * closed by Escape or a click outside.
 */
import type React from "react"
import { useId, useState } from "react"

import InfoOutlined from "@mui/icons-material/InfoOutlined"
import { IconButton, Popover, Stack, Tooltip, Typography } from "@mui/material"

export const InfoPopover: React.FC<
  React.PropsWithChildren<{
    /** Accessible name of the ⓘ button. */
    label: string
    /** Heading shown at the top of the card. */
    title: string
  }>
> = ({ label, title, children }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const popoverId = useId()
  const titleId = useId()
  const open = Boolean(anchorEl)

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          aria-controls={open ? popoverId : undefined}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={label}
          onClick={(event) => {
            setAnchorEl(event.currentTarget)
          }}
          size="small"
        >
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        id={open ? popoverId : undefined}
        onClose={() => {
          setAnchorEl(null)
        }}
        open={open}
        slotProps={{
          paper: { "aria-labelledby": titleId, role: "dialog" },
        }}
      >
        <Stack spacing={1.5} sx={{ maxWidth: 420, p: 2.5 }}>
          <Typography
            component="h4"
            id={titleId}
            sx={{ fontWeight: "bold" }}
            variant="body1"
          >
            {title}
          </Typography>
          {children}
        </Stack>
      </Popover>
    </>
  )
}
